import { SelectChangeEvent } from '@mui/material';
import { addDays, format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ConfirmUserHereWithUserIdMutation,
  User,
} from '../../apollo/__generated__/graphql';
import { useConfirmUserHereMutation } from '../../event/hooks/useConfirmUserHereMutation';
import { useCurrentEventQuery } from '../../event/hooks/useCurrentEventQuery';
import { useUnconfirmUserHereMutation } from '../../event/hooks/useUnconfirmUserHereMutation';
import { useUpdateCaution } from '../../event/hooks/useUpdateCaution';
import { useUpdatePayment } from '../../event/hooks/useUpdatePayment';
import { useUpdateValidation } from '../../event/hooks/useUpdateValidation';
import { useValidatorEventUsersQuery } from '../../event/hooks/useValidatorEventUsersQuery';
import { useToast } from '../../toast/Toast.context';
import {
  CheckboxFilterType,
  DialogType,
} from '../AdminEventUsers/useAdminEventUsers';

const nextFilterByFilterType: Record<CheckboxFilterType, CheckboxFilterType> = {
  only: 'only-not',
  'only-not': 'all',
  all: 'only',
};

export const useAdminValidation = () => {
  const [search, setSearch] = useState<string>('');
  const [cautionFilter, setCautionFilter] = useState<CheckboxFilterType>('all');
  const [paiementFilter, setPaiementFilter] =
    useState<CheckboxFilterType>('all');
  const [validationFilter, setValidationFilter] =
    useState<CheckboxFilterType>('all');
  const [isHereFilter, setIsHereFilter] = useState<CheckboxFilterType>('all');
  const [arrivalDayFilter, setArrivalDayFilter] = useState<string>('all');

  const [isQrCodeModalOpen, setIsQrCodeModalOpen] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [dialog, setDialog] = useState<DialogType | null>(null);
  const [lastConfirmedUser, setLastConfirmedUser] = useState<
    ConfirmUserHereWithUserIdMutation['confirmUserHere'] | null
  >(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const { showToast } = useToast();
  const [isRecapModalOpen, setIsRecapModalOpen] = useState<boolean>(false);

  const { data: currentEventData } = useCurrentEventQuery();
  const { data: eventUsersData, loading } = useValidatorEventUsersQuery(
    currentEventData?.currentEvent?.id ?? null,
  );

  const removeUrlParams = () => {
    searchParams.delete('code');
    setSearchParams(searchParams);
  };

  const onUserConfirmed = (
    user: ConfirmUserHereWithUserIdMutation['confirmUserHere'],
  ) => {
    removeUrlParams();

    setLastConfirmedUser(user);
    setIsRecapModalOpen(true);
  };

  const onConfirmUserError = () => {
    removeUrlParams();
  };

  const { confirmUserHere } = useConfirmUserHereMutation(
    onUserConfirmed,
    onConfirmUserError,
  );
  const { unconfirmUserHere } = useUnconfirmUserHereMutation();

  const { onCautionPress } = useUpdateCaution(setDialog);
  const { onClosePaymentDialog, onPaymentPress, paymentDialogInformation } =
    useUpdatePayment(setDialog);
  const { onValidatePress } = useUpdateValidation(setDialog);

  const clearCode = () => {
    setCode('');
  };

  const onCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value);
  };

  useEffect(() => {
    if (searchParams) {
      const code = searchParams.get('code');
      if (code) {
        confirmUserHere({ code });
      }
    }
  }, [searchParams, confirmUserHere]);

  const onSubmit = () => {
    if (code.length !== 6) {
      showToast({
        type: 'error',
        message: 'Le code doit contenir 6 caractères',
      });
      return;
    }
    confirmUserHere({ code });
    setCode('');
  };

  const onHerePress = (user: User) => {
    if (user.isHere) {
      unconfirmUserHere(user.id);
    } else {
      confirmUserHere({ userId: user.id });
    }
  };

  const eventNumberOfDays = useMemo(
    () => currentEventData?.currentEvent?.numberOfDays ?? 0,
    [currentEventData],
  );

  const getArrivalDayNameByDay = useCallback(
    (day: number) => {
      if (!currentEventData?.currentEvent) {
        return '';
      }

      const date = addDays(currentEventData.currentEvent.startAt, day);
      return format(date, 'eeee', { locale: fr });
    },
    [currentEventData],
  );

  const arrivalDayOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i < eventNumberOfDays; i += 1) {
      options.push({
        label: getArrivalDayNameByDay(i),
        value: String(i),
      });
    }
    return options;
  }, [eventNumberOfDays, getArrivalDayNameByDay]);

  const eventUsers = useMemo(
    () =>
      (eventUsersData?.eventUsers ?? [])
        .filter((user) => {
          if (cautionFilter === 'all') {
            return true;
          }

          if (cautionFilter === 'only') {
            return user.hasCaution;
          }

          return !user.hasCaution;
        })
        .filter((user) => {
          if (paiementFilter === 'all') {
            return true;
          }

          if (paiementFilter === 'only') {
            return user.hasPaidTicket;
          }

          return !user.hasPaidTicket;
        })
        .filter((user) => {
          if (validationFilter === 'all') {
            return true;
          }

          if (validationFilter === 'only') {
            return user.isValidated;
          }

          return !user.isValidated;
        })
        .filter((user) => {
          if (isHereFilter === 'all') {
            return true;
          }

          if (isHereFilter === 'only') {
            return user.isHere;
          }

          return !user.isHere;
        })
        .filter((user) => {
          if (arrivalDayFilter === 'all') {
            return true;
          }

          const arrivalDay = Number(arrivalDayFilter);

          return user.arrivalDay === arrivalDay;
        })
        .filter((eventUser) =>
          `${eventUser.firstname} ${eventUser.lastname}. ${eventUser.nickname} ${eventUser.studyTown}`
            .toLowerCase()
            .includes(search.toLowerCase()),
        ),
    [
      eventUsersData,
      validationFilter,
      cautionFilter,
      isHereFilter,
      paiementFilter,
      search,
      arrivalDayFilter,
    ],
  );

  const onCloseDialog = () => {
    setDialog(null);
  };

  const onRecapModalClose = () => {
    setIsRecapModalOpen(false);
  };

  const onOpenQrCodeScanner = () => {
    setIsQrCodeModalOpen(true);
  };

  const onCloseQrCodeScanner = () => {
    setIsQrCodeModalOpen(false);
  };

  const onQrCodeSan = (result: string) => {
    const code = result.split('?code=')[1];
    if (code) {
      confirmUserHere({ code });
      onCloseQrCodeScanner();
    }
  };

  const onCautionFilterCheckboxChange = () => {
    setCautionFilter(nextFilterByFilterType[cautionFilter]);
  };

  const onPaiementFilterCheckboxChange = () => {
    setPaiementFilter(nextFilterByFilterType[paiementFilter]);
  };

  const onValidationFilterCheckboxChange = () => {
    setValidationFilter(nextFilterByFilterType[validationFilter]);
  };

  const onIsHereFilterCheckboxChange = () => {
    setIsHereFilter(nextFilterByFilterType[isHereFilter]);
  };

  const clearSearch = () => {
    setSearch('');
  };

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const onArrivalDayFilterChange = (event: SelectChangeEvent) => {
    setArrivalDayFilter(event.target.value);
  };

  return {
    dialog,
    eventUsers,
    loading,
    code,
    clearCode,
    onCodeChange,
    onSubmit,
    onHerePress,
    onCautionPress,
    onCloseDialog,
    onPaymentPress,
    onValidatePress,
    onClosePaymentDialog,
    paymentDialogInformation,
    onRecapModalClose,
    isRecapModalOpen,
    lastConfirmedUser,
    onOpenQrCodeScanner,
    isQrCodeModalOpen,
    onCloseQrCodeScanner,
    onQrCodeSan,
    getArrivalDayNameByDay,
    onPaiementFilterCheckboxChange,
    paiementFilter,
    cautionFilter,
    onCautionFilterCheckboxChange,
    onValidationFilterCheckboxChange,
    onIsHereFilterCheckboxChange,
    isHereFilter,
    validationFilter,
    clearSearch,
    onSearchChange,
    search,
    arrivalDayOptions,
    arrivalDayFilter,
    onArrivalDayFilterChange,
  };
};
